import * as Sentry from '@sentry/react';

const API_BASE = import.meta.env.VITE_API_URL || '';
const DEV_AUTH_BYPASS_ACTIVE =
  import.meta.env.DEV && import.meta.env.VITE_DEV_BYPASS_AUTH === '1';

let identityHeaders: Record<string, string> = {};

// ── Session expiry verification ──
// A 401 can mean "session expired" OR "insufficient permissions" (e.g. non-admin
// hitting /api/admin/*). Blindly redirecting on every 401 causes a login loop.
// Instead, verify the session via /api/auth/me before dispatching the redirect
// event. At most one verification per 5 seconds to avoid flooding.
let _sessionCheckInFlight = false;
let _lastSessionCheckMs = 0;
let _suppressSessionExpiryChecksUntilMs = 0;

export function suppressSessionExpiryChecks(durationMs: number) {
  const safeDurationMs = Number.isFinite(durationMs) ? Math.max(0, durationMs) : 0;
  _suppressSessionExpiryChecksUntilMs = Date.now() + safeDurationMs;
}

async function verifySessionExpired(): Promise<void> {
  if (DEV_AUTH_BYPASS_ACTIVE) return;
  const now = Date.now();
  if (now < _suppressSessionExpiryChecksUntilMs) return;
  if (_sessionCheckInFlight || now - _lastSessionCheckMs < 5_000) return;
  _sessionCheckInFlight = true;
  _lastSessionCheckMs = now;
  try {
    const res = await fetch(`${API_BASE}/api/auth/me`, { credentials: 'include' });
    if (res.status === 401) {
      // Session is truly expired — redirect to login.
      window.dispatchEvent(new CustomEvent('vaerion:session-expired'));
    }
    // 200 → session is valid, the 401 was just a permission issue. No redirect.
  } catch {
    // Network error — conservatively assume session may be expired.
    window.dispatchEvent(new CustomEvent('vaerion:session-expired'));
  } finally {
    _sessionCheckInFlight = false;
  }
}

export function setIdentityHeaders(headers: Record<string, string>) {
  identityHeaders = headers;
}

export function clearIdentityHeaders() {
  identityHeaders = {};
}

export function getIdentityHeaders(): Record<string, string> {
  return { ...identityHeaders };
}

class ApiRequestError extends Error {
  status: number;
  code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = code;
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = {};

  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }

  // Attach identity headers on all API requests so session-exempt routes
  // (operator, admin, orchestrator, internal, trust, etc.) receive tenant/actor info
  if (path.startsWith('/api/')) {
    Object.assign(headers, identityHeaders);
  }

  const url = path.startsWith('/') ? `${API_BASE}${path}` : path;

  const res = await fetch(url, {
    method,
    headers,
    credentials: 'include',
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    let errorMsg = res.statusText;
    let code: string | undefined;
    try {
      const err = await res.json();
      if (err && typeof err === 'object') {
        const bodyError = (err as { error?: string }).error;
        // Guard against backends that return {"error": "undefined"} or empty string
        if (bodyError && bodyError !== 'undefined') errorMsg = bodyError;
        code = (err as { code?: string }).code;
      }
    } catch {
      // response body was not JSON
    }
    const apiError = new ApiRequestError(errorMsg, res.status, code);

    // 401 on a non-auth endpoint MAY mean session expired, but could also mean
    // insufficient permissions (e.g. non-admin hitting /api/admin/*). Verify the
    // session before redirecting to avoid a login loop.
    if (res.status === 401) {
      const isAuthEndpoint =
        path.includes('/api/auth/') || path.includes('/api/portal/auth/');
      if (!isAuthEndpoint && !DEV_AUTH_BYPASS_ACTIVE) {
        verifySessionExpired();
      }
    }

    // Report server errors (5xx) to Sentry — skip 4xx (expected client errors / PHI-safe)
    if (res.status >= 500) {
      Sentry.captureException(apiError, {
        extra: { method, status: res.status, code }, // intentionally omit URL (may contain PHI)
      });
    }
    throw apiError;
  }

  if (res.status === 204) return undefined as T;

  return res.json() as Promise<T>;
}

export function get<T>(path: string): Promise<T> {
  return request<T>('GET', path);
}

export function post<T>(path: string, body?: unknown): Promise<T> {
  return request<T>('POST', path, body);
}

export function put<T>(path: string, body?: unknown): Promise<T> {
  return request<T>('PUT', path, body);
}

export function del<T>(path: string): Promise<T> {
  return request<T>('DELETE', path);
}

export function patch<T>(path: string, body?: unknown): Promise<T> {
  return request<T>('PATCH', path, body);
}

export { ApiRequestError };
